import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  TabPane,
  CardFooter,
} from 'reactstrap';
import PageTabs from 'components/PageTabs';
import { FixedCenterLoader } from 'components/Loaders';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import DownloadSimpleTable from 'components/DownloadSimpleTable';
import ReactDatetime from 'react-datetime';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getCorrelationMatrix } from 'services/analysis';
import { optionizeValue, optionizeAll, getValue, getDate, getYearMonth, monthDiff } from 'helpers/select';
import { confidenceLevelToInterval } from 'helpers/formatter';
import { toDeci, toPerc, toPercTable, toDeciTable, tablePresets, getDataType, formatCurrency } from 'helpers/formatter';
import PieChart from 'components/PieChart';
import TightTable from 'components/TightTable';
import { Descriptions, Notes } from '../others';
import MultipleAssetSelect from '../MultipleAssetSelect';
import CorrelationMatrixBase from './correlation-matrix/CorrelationMatrix';
import AssetHeader from 'views/AssetHeader';
import ToggleContainer from 'components/ToggleContainer';

function CorrelationMatrix({ session }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ product: [], database: [], window: '12' });
  const [result, setResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    const [data, err] = await getCorrelationMatrix(options);

    setResult(data);
    setLoading(false);
  };

  useEffect(() => {
    if (options.product.length === 0) return;
    fetchData();
  }, [options]);

  let main = null;
  if (loading) {
    main = <FixedCenterLoader />;
  } else if (result) {
    main = (
      <>
        <div className="tw-text-3xl tw-font-bold tw-mb-1 tw-text-center">Correlation Matrix</div>
        <Descriptions content={result.description} />
        <CorrelationMatrixBase data={result.data} params={options} />
        <Notes content={result.notes} />
      </>
    );
  }

  const minItems = session?.permissions['co-correlation-matrix-min-items'] ?? 5;
  const maxItems = session?.permissions['co-correlation-matrix-max-items'] ?? 5;

  const sideMenu = (
    <MultipleAssetSelect
      session={session}
      onSelect={({ assets, databases, period }) => {
        setOptions({ product: assets, database: databases, window: period });
      }}
      title={`SELECT ASSETS (Min ${minItems}, Max ${maxItems})`}
      minItems={minItems}
      maxItems={maxItems}
    />
  );

  const mainContent = <div key={options.product.join('_')}>{main}</div>;

  return (
    <ToggleContainer
      sideMenu={sideMenu}
      mainContent={options.product.length > 0 && mainContent}
      classNames={{ sideMenu: 'tw-w-[450px]' }}
    />
  );
}

export default CorrelationMatrix;
